import * as React from 'react';
import {createTranslationFn} from '../utils';
import type {ContentKey} from '../types';
import type {TranslationFn, TranslationStringMap} from '../utils/types';

const TranslationContext = React.createContext<TranslationFn<ContentKey>>(
  () => ''
);

type TranslationProviderProps = {
  translations: TranslationStringMap;
  children?: React.ReactNode;
};

/**
 * Used at the top level of the Designer app to inject the translation content to make it available for the rest of the app.
 */
export const TranslationProvider = ({
  children,
  translations,
}: TranslationProviderProps) => {
  const translationFn = React.useMemo(
    () => createTranslationFn<ContentKey>(translations),
    [translations]
  );

  return (
    <TranslationContext.Provider value={translationFn}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Use this hook when translating keys in Functional React components. This should be your most common use case.
 * When using in class components, you can use the `<Translation />` component instead.
 */
export const useTranslation = (): TranslationFn<ContentKey> => {
  const t = React.useContext(TranslationContext);
  return t;
};
